/* eslint-disable jsx-a11y/anchor-is-valid */
import { Link } from "react-router-dom";
import { DTO } from "src/common/utils/types";
import {
  initializeObtinedViaForCurrentAccessControl,
  ObtainedViaForCurrentAccessControl
} from "./obtainedViaForAccessControl";

type ResourceForCurrentAccessControl = {
  id: string;
  name: string;
  group: string;
  domain: string;
  attributes: Map<string, string>;
  countOfSystemResources: string;
};

export class CurrentAccessControlBasedOnInformationAsset {
  static mandatoryAttributes = [
    "resource",
    "obtainedVia",
  ];
  resource: ResourceForCurrentAccessControl;
  obtainedVia: ObtainedViaForCurrentAccessControl;
  rowSpan: { obtainedViaSpan: number; resourceSpan: number };

  constructor(input: Object) {
    for (const [key, value] of Object.entries(input)) {
      if (key !== "obtainedVia" && key !== "systemResourceAttributes") {
        this[key] = value;
      }
    }
    const inputKeys = Array.from(Object.keys(input));
    const missedMandatoryFields =
      CurrentAccessControlBasedOnInformationAsset.mandatoryAttributes.filter(
        (mandatoryAttribute) => !inputKeys.includes(mandatoryAttribute),
      );
    if (missedMandatoryFields.length > 0) {
      console.log(missedMandatoryFields.join(", "));
      throw new Error(
        `Following fields are not present on creating Current Access Control Based On System Resource object ${missedMandatoryFields.join(
          ", ",
        )}`,
      );
    }
    this.obtainedVia = initializeObtinedViaForCurrentAccessControl(
      input["obtainedVia"] as ObtainedViaForCurrentAccessControl,
    );
  }

  resourceId(): string {
    return this.resource.id;
  }

  obtainedViaCombinedId(): string {
    return this.obtainedVia.stringifiedId();
  }

  calculateIdentifiers(): { resource: string; obtainedVia: string } {
    return {
      resource: this.resourceId(),
      obtainedVia: this.obtainedViaCombinedId(),
    };
  }

  renderAttribute(name: string): String {
    return this.resource.attributes[name] || "--";
  }

  countOfSystemResources(updatedUrl: string, filteredSystemResourceType: string): JSX.Element {
    const countOfSystemResources = this.resource.countOfSystemResources;
    return (
      <div style={{ display: "flex" }} className="count-of-system-resource-cell">
            <Link to={updatedUrl}>
              {`${filteredSystemResourceType} (${countOfSystemResources})`}
            </Link>
      </div>
    );
  }

  setRowSpan(rowSpan: {
    obtainedViaSpan: number;
    resourceSpan: number;
  }) {
    this.rowSpan = rowSpan;
  }
}

export class CurrentAccessControlsBasedOnInformationAssets {
  currentAccessControls: Array<CurrentAccessControlBasedOnInformationAsset>;

  constructor(inputs: Array<DTO<CurrentAccessControlBasedOnInformationAsset>>) {
    this.currentAccessControls = inputs.map(
      (input) => new CurrentAccessControlBasedOnInformationAsset(input),
    );
    this.calculateRowSpans();
  }

  calculateRowSpans() {
    const obtainedViaSpan = new Map<number, number>();
    const resourceSpan = new Map<number, number>();
    if (this.currentAccessControls.length === 0) {
      return;
    }
    let previousIdentifiers = this.currentAccessControls[0].calculateIdentifiers();
    let lastRepeatedResourceCount = 1;
    let lastRepeatedObtainedViaCount = 1;
    let index: number = 1;
    for (index; index < this.currentAccessControls.length; index++) {
      const currentIdentifiers = this.currentAccessControls[index].calculateIdentifiers();
      if (currentIdentifiers.obtainedVia === previousIdentifiers.obtainedVia) {
        lastRepeatedObtainedViaCount = lastRepeatedObtainedViaCount + 1;
      } else {
        obtainedViaSpan.set(index - lastRepeatedObtainedViaCount, lastRepeatedObtainedViaCount);
        lastRepeatedObtainedViaCount = 1;
      }
      if (currentIdentifiers.resource === previousIdentifiers.resource) {
        lastRepeatedResourceCount = lastRepeatedResourceCount + 1;
      } else {
        resourceSpan.set(index - lastRepeatedResourceCount, lastRepeatedResourceCount);
        lastRepeatedResourceCount = 1;
      }
      previousIdentifiers = this.currentAccessControls[index].calculateIdentifiers();
    }
    obtainedViaSpan.set(index - lastRepeatedObtainedViaCount, lastRepeatedObtainedViaCount);
    resourceSpan.set(index - lastRepeatedResourceCount, lastRepeatedResourceCount);
    this.currentAccessControls.forEach((value, index) => {
      value.setRowSpan({
        obtainedViaSpan: obtainedViaSpan.get(index) ?? 0,
        resourceSpan: resourceSpan.get(index) ?? 0,
      });
    });
  }
}
